import React, { useState, useEffect, useRef } from 'react';
import { Slide, SlideElement, Theme, AspectRatio, ThemeFont, SlideBorderStyle } from '../types';
import { ThemeSelector } from './ThemeSelector';
import { BackgroundController } from './BackgroundController';
import { TRANSLATIONS } from '../constants';
import { LayoutOption, getLayoutOptionsForObjectCount, countContentObjects } from '../utils/dynamicLayout';
import { THEME_FONTS, ThemeFontSelector } from './ThemeFontSelector';
import { SLIDE_BORDER_STYLES, SlideBoxStyleSelector } from './SlideBoxStyleSelector';
import {
  Type,
  Shapes,
  MessageSquare,
  BarChart3,
  Image,
  Video,
  Layers,
  Plus,
  Palette,
  Layout,
  Grid3X3,
  Columns2,
  Columns3,
  MoreHorizontal,
  AlignLeft,
  AlignRight,
  AlignVerticalJustifyStart,
  AlignVerticalJustifyEnd,
  Slash,
  Menu,
  X,
  PlusCircle
} from 'lucide-react';

// Types from SlideThemeSelector - exported for other components
export type SlideTheme = 'default' | 'left-split' | 'right-split' | 'top-split' | 'bottom-split' | 'diagonal';
export type DividerType = 'straight' | 'convex' | 'concave' | 'zigzag' | 'cloud' | 'jagged' | 'gradient' | 'random';

interface SlideThemeOption {
  id: SlideTheme;
  name: {
    ko: string;
    en: string;
  };
  description: {
    ko: string;
    en: string;
  };
  icon: React.ReactNode;
}

interface DividerTypeOption {
  id: DividerType;
  name: {
    ko: string;
    en: string;
  };
  description: {
    ko: string;
    en: string;
  };
}

const SLIDE_THEME_OPTIONS: SlideThemeOption[] = [
  {
    id: 'default',
    name: {
      ko: '기본',
      en: 'Def'
    },
    description: {
      ko: '배경이미지가 전체에 깔림',
      en: 'Background image covers entire slide'
    },
    icon: <Layout className="w-4 h-4" />
  },
  {
    id: 'left-split',
    name: {
      ko: '좌측',
      en: 'L'
    },
    description: {
      ko: '좌측 1:3 분할, 좌측 이미지',
      en: '1:3 split, image on left'
    },
    icon: <AlignLeft className="w-4 h-4" />
  },
  {
    id: 'right-split',
    name: {
      ko: '우측',
      en: 'R'
    },
    description: {
      ko: '우측 3:1 분할, 우측 이미지',
      en: '3:1 split, image on right'
    },
    icon: <AlignRight className="w-4 h-4" />
  },
  {
    id: 'top-split',
    name: {
      ko: '상단',
      en: 'Top'
    },
    description: {
      ko: '상단 1:4 분할, 상단 이미지',
      en: '1:4 split, image on top'
    },
    icon: <AlignVerticalJustifyStart className="w-4 h-4" />
  },
  {
    id: 'bottom-split',
    name: {
      ko: '하단',
      en: 'Btm'
    },
    description: {
      ko: '하단 4:1 분할, 하단 이미지',
      en: '4:1 split, image on bottom'
    },
    icon: <AlignVerticalJustifyEnd className="w-4 h-4" />
  },
  {
    id: 'diagonal',
    name: {
      ko: '사선',
      en: ' / '
    },
    description: {
      ko: '좌측 사선 분할',
      en: 'Diagonal split'
    },
    icon: <Slash className="w-4 h-4" />
  }
];

const DIVIDER_TYPE_OPTIONS: DividerTypeOption[] = [
  {
    id: 'straight',
    name: {
      ko: '일직선',
      en: 'Straight'
    },
    description: {
      ko: '일직선으로 분할',
      en: 'Straight line division'
    }
  },
  {
    id: 'convex',
    name: {
      ko: '볼록',
      en: 'Convex'
    },
    description: {
      ko: '볼록한 곡선 형태',
      en: 'Convex curve shape'
    }
  },
  {
    id: 'concave',
    name: {
      ko: '오목',
      en: 'Concave'
    },
    description: {
      ko: '오목한 곡선 형태',
      en: 'Concave curve shape'
    }
  },
  {
    id: 'zigzag',
    name: {
      ko: '지그재그',
      en: 'Zigzag'
    },
    description: {
      ko: '지그재그 형태',
      en: 'Zigzag pattern'
    }
  },
  {
    id: 'cloud',
    name: {
      ko: '구름',
      en: 'Cloud'
    },
    description: {
      ko: '구름처럼 여러 곡선',
      en: 'Multiple curves like clouds'
    }
  },
  {
    id: 'jagged',
    name: {
      ko: '요철',
      en: 'Jagged'
    },
    description: {
      ko: '요철 모양 형태',
      en: 'Jagged pattern'
    }
  },
  {
    id: 'gradient',
    name: {
      ko: '그라데이션',
      en: 'Gradient'
    },
    description: {
      ko: '서서히 변화하는 경계',
      en: 'Gradual transition'
    }
  },
  {
    id: 'random',
    name: {
      ko: '랜덤',
      en: 'Random'
    },
    description: {
      ko: '랜덤한 기하학적 형태',
      en: 'Random geometric shape'
    }
  }
];

// Helper functions from DynamicLayoutSelector
const getLayoutIcon = (layoutType: string) => {
  switch (layoutType) {
    case 'single-column':
      return <MoreHorizontal className="w-4 h-4 rotate-90" />;
    case 'two-column-equal':
    case 'two-column-1-2':
    case 'two-column-2-1':
    case 'two-column-1-3':
    case 'two-column-3-1':
    case 'two-column-1-4':
    case 'two-column-2-3':
    case 'two-column-3-2':
    case 'two-column-4-1':
    case 'two-column-1-5':
    case 'two-column-2-4':
      return <Columns2 className="w-4 h-4" />;
    case 'three-column':
    case 'three-column-1-2-1':
    case 'three-column-1-2-2':
    case 'three-column-1-2-3':
    case 'three-column-equal':
    case 'three-column-2-1-3':
      return <Columns3 className="w-4 h-4" />;
    case 'four-column':
      return <Grid3X3 className="w-4 h-4" />;
    default:
      return <Layout className="w-4 h-4" />;
  }
};

// Layout preview component from DynamicLayoutSelector
const LayoutPreview: React.FC<{ 
  columns: number[]; 
  isSelected: boolean; 
  isDarkMode: boolean 
}> = ({ columns, isSelected, isDarkMode }) => {
  const totalCols = columns.length;
  const maxItemsInCol = Math.max(...columns);
  
  return (
    <div className={`w-6 h-5 border rounded flex ${
      isSelected 
        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' 
        : `border-gray-300 ${isDarkMode ? 'dark:border-gray-600' : ''}`
    } ${isDarkMode ? 'dark:bg-gray-800' : 'bg-white'}`}>
      {columns.map((itemCount, colIndex) => (
        <div 
          key={colIndex}
          className="flex-1 flex flex-col justify-evenly p-0.5"
        >
          {Array.from({ length: itemCount }).map((_, itemIndex) => (
            <div
              key={itemIndex}
              className={`rounded-sm ${
                isSelected 
                  ? 'bg-blue-400' 
                  : isDarkMode 
                    ? 'bg-gray-600' 
                    : 'bg-gray-400'
              }`}
              style={{ height: `${80 / maxItemsInCol}%` }}
            />
          ))}
        </div>
      ))}
    </div>
  );
};

interface SlideEditingPanelProps {
  tempSlide: Slide;
  theme: Theme;
  aspectRatio: AspectRatio;
  selectedLayoutOption?: LayoutOption;
  layoutSpacing: 'none' | 'narrow' | 'normal' | 'wide';
  includeTitleInLayout: boolean;
  isDarkMode: boolean;
  language: 'ko' | 'en';
  showPixabaySearch: boolean;
  activeSection: 'elements' | 'theme' | 'background';
  onActiveSectionChange: (section: 'elements' | 'theme' | 'background') => void;
  onThemeChange: (newTheme: Theme) => void;
  onSlideThemeChange: (newTheme: SlideTheme) => void;
  onSplitPositionChange: (newPosition: number) => void;
  onContentBgColorChange: (newColor: string) => void;
  onDividerTypeChange: (newType: 'straight' | 'convex' | 'concave' | 'zigzag' | 'jagged' | 'gradient') => void;
  onDynamicLayoutChange: (option: LayoutOption) => void;
  onLayoutSpacingChange: (spacing: 'none' | 'narrow' | 'normal' | 'wide') => void;
  onIncludeTitleInLayoutChange: (include: boolean) => void;
  onBackgroundSeedChange: (seed: string) => void;
  onBackgroundBlurChange: (blur: number) => void;
  onBackgroundGrayscaleChange: (grayscale: boolean) => void;
  onBackgroundChange: (background: string) => void;
  onBackgroundPatternChange: (pattern: string) => void;
  onBackgroundTypeChange: (type: 'image' | 'color' | 'animated') => void;
  onAnimatedEffectChange: (effect: string) => void;
  onAnimatedOptionsChange: (options: {
    backgroundColor?: string;
    color1?: string;
    color2?: string;
    scale?: number;
    speed?: number;
  }) => void;
  onImageElementAdd: (imageUrl: string) => void;
  setShowPixabaySearch: (show: boolean) => void;
  slides: Slide[];
  activeSlideId: string;
  activeTab: 'preview' | 'code';
  showPromptPanel: boolean;
  displayScale: number;
  onTabChange: (tab: 'preview' | 'code') => void;
  onSlideMove: (fromIndex: number, toIndex: number) => void;
  onSlideSelect: (slideId: string) => void;
  onPromptPanelToggle: () => void;
  onDisplayScaleChange: (scale: number) => void;
  onSaveAndExit: () => void;
  onTextElementAdd: () => void;
  onShapeElementAdd: () => void;
  onChartElementAdd: () => void;
  onImageElementUpload: () => void;
  onVideoElementUpload: () => void;
  onLayerPanelOpen: () => void;
  onRightPanelToggle?: () => void;
  themeFont?: ThemeFont;
  onThemeFontChange: (font: ThemeFont) => void;
  slideBorderStyle?: SlideBorderStyle;
  onSlideBorderStyleChange: (style: SlideBorderStyle) => void;
  onFontBorderChange?: (enabled: boolean) => void;
  onFontGlowChange?: (enabled: boolean) => void;
}

export const SlideEditingPanel: React.FC<SlideEditingPanelProps> = ({
  tempSlide,
  theme,
  aspectRatio,
  selectedLayoutOption,
  layoutSpacing,
  includeTitleInLayout,
  isDarkMode,
  language,
  showPixabaySearch,
  activeSection,
  onActiveSectionChange,
  onThemeChange,
  onSlideThemeChange,
  onSplitPositionChange,
  onContentBgColorChange,
  onDividerTypeChange,
  onDynamicLayoutChange,
  onLayoutSpacingChange,
  onIncludeTitleInLayoutChange,
  onBackgroundSeedChange,
  onBackgroundBlurChange,
  onBackgroundGrayscaleChange,
  onBackgroundChange,
  onBackgroundPatternChange,
  onBackgroundTypeChange,
  onAnimatedEffectChange,
  onAnimatedOptionsChange,
  onImageElementAdd,
  setShowPixabaySearch,
  slides,
  activeSlideId,
  activeTab,
  showPromptPanel,
  displayScale,
  onTabChange,
  onSlideMove,
  onSlideSelect,
  onPromptPanelToggle,
  onDisplayScaleChange,
  onSaveAndExit,
  onTextElementAdd,
  onShapeElementAdd,
  onChartElementAdd,
  onImageElementUpload,
  onVideoElementUpload,
  onLayerPanelOpen,
  onRightPanelToggle,
  themeFont,
  onThemeFontChange,
  slideBorderStyle,
  onSlideBorderStyleChange,
  onFontBorderChange,
  onFontGlowChange
}) => {
  const t = TRANSLATIONS[language];
  
  // State from DynamicLayoutSelector
  const [hasInitialized, setHasInitialized] = React.useState(false);
  
  // State from SlideThemeSelector
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [customColor, setCustomColor] = useState(tempSlide.contentBgColor || '#E5E7EB');
  const [previousSlideTheme, setPreviousSlideTheme] = useState(tempSlide.slideTheme);
  const [fontDropdownOpen, setFontDropdownOpen] = useState(false);
  const [fontPreview, setFontPreview] = useState<ThemeFont | null>(null);
  const [boxStyleDropdownOpen, setBoxStyleDropdownOpen] = useState(false);
  const [boxStylePreview, setBoxStylePreview] = useState<typeof SLIDE_BORDER_STYLES[0] | null>(null);
  
  // 드롭다운 메뉴 위치 계산을 위한 ref
  const fontDropdownRef = useRef<HTMLButtonElement>(null);
  const boxStyleDropdownRef = useRef<HTMLButtonElement>(null);
  
  // 모바일 모달 상태
  const [showDynamicLayoutModal, setShowDynamicLayoutModal] = useState(false);
  const [showSlideThemeModal, setShowSlideThemeModal] = useState(false);

  // tempSlide.id를 기준으로 현재 슬라이드 인덱스 계산
  const currentSlideIndex = slides.findIndex(s => s.id === tempSlide.id);

  const getPreviousSlideBackgroundSeed = () => {
    if (currentSlideIndex > 0) {
      const prevSlide = slides[currentSlideIndex - 1];
      // 배경 이미지 URL에서 시드 추출
      if (prevSlide?.backgroundImage && prevSlide.backgroundImage.includes('picsum.photos/seed/')) {
        const seedMatch = prevSlide.backgroundImage.match(/seed\/([^/]+)\//); 
        if (seedMatch) {
          return seedMatch[1];
        }
      }
      return prevSlide?.backgroundSeed;
    }
    return undefined;
  };

  // 현재 슬라이드의 배경 시드 추출
  const getCurrentBackgroundSeed = () => {
    // 배경 이미지 URL에서 시드 추출
    if (tempSlide.backgroundImage && tempSlide.backgroundImage.includes('picsum.photos/seed/')) {
      const seedMatch = tempSlide.backgroundImage.match(/seed\/([^/]+)\//); 
      if (seedMatch) {
        return seedMatch[1];
      }
    }
    // backgroundSeed가 있으면 그것 사용, 없으면 기본값 생성
    return tempSlide.backgroundSeed || `slide-${tempSlide.id}`;
  };

  // Dynamic Layout logic
  const objectCount = countContentObjects(tempSlide.elements || [], 15);
  const layoutOptions = getLayoutOptionsForObjectCount(objectCount);
  
  // Initialize layout option only once
  React.useEffect(() => {
    if (layoutOptions.length > 0 && !selectedLayoutOption && !hasInitialized) {
      if (layoutOptions[0]) {
        onDynamicLayoutChange(layoutOptions[0]);
        setHasInitialized(true);
      }
    }
  }, [hasInitialized]); // Remove dependencies that cause re-initialization
  
  // Update previous slide theme when it changes
  React.useEffect(() => {
    if (tempSlide.slideTheme !== previousSlideTheme) {
      setPreviousSlideTheme(tempSlide.slideTheme);
    }
  }, [tempSlide.slideTheme, previousSlideTheme]);

  const spacingIcons = ['○', '◐', '◑', '●'];
  const dividerOptions = [
    { value: 'straight', icon: '━', label: language === 'ko' ? '직선' : 'Straight' },
    { value: 'wavy', icon: '〰', label: language === 'ko' ? '물결' : 'Wavy' },
    { value: 'jagged', icon: '⚡', label: language === 'ko' ? '톱니' : 'Jagged' }
  ];

  // 폰트 스타일과 박스 스타일 드롭다운 참조 (중복 선언 제거)

  // 외부 클릭 감지하여 드롭다운 닫기
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (fontDropdownRef.current && !fontDropdownRef.current.contains(event.target as Node)) {
        setFontDropdownOpen(false);
      }
      if (boxStyleDropdownRef.current && !boxStyleDropdownRef.current.contains(event.target as Node)) {
        setBoxStyleDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <>
      <div
          className={`flex flex-col md:flex-row min-h-[120px] md:min-h-[140px] lg:min-h-[160px] relative ${
            isDarkMode ? 'bg-gray-950 text-white' : 'bg-white text-gray-900'
          } shadow-lg`}
      >
        {/* 모바일: 상단 영역, 데스크톱: 좌측 영역 */}
        <div className="flex md:flex-col flex-row md:block">
          {/* 모바일에서 탭바를 네비게이션 옆에 배치 */}
          <div className={`flex md:hidden flex-1 items-center justify-between p-1 ${
            isDarkMode ? 'bg-gray-900' : 'bg-gray-100'
          } overflow-x-auto`}
          >
            <div className="flex items-center gap-2 flex-nowrap">
              {/* 슬라이드 네비게이션 - 가장 좌측에 배치 */}
              <div className={`flex flex-col items-center gap-0 p-0 rounded ${
                isDarkMode ? 'bg-gray-800' : 'bg-gray-200'
              }`}>
                <div className="flex items-center">
                  <span className={`text-sm font-light ${
                    isDarkMode ? 'text-blue-400' : 'text-blue-600'
                  }`}>
                    {currentSlideIndex + 1}
                  </span>
                  <span className={`text-[8px] ml-1 ${
                    isDarkMode ? 'text-gray-500' : 'text-gray-600'
                  }`}>
                    / {slides.length}
                  </span>
                </div>
                <div className="flex gap-1">
                  <button
                    onClick={() => {
                      if (currentSlideIndex > 0) {
                        onSlideMove(currentSlideIndex, currentSlideIndex - 1);
                        // 슬라이드 이동 후 자동 스크롤
                        setTimeout(() => {
                          const slideElement = document.querySelector(`[data-slide-id="${tempSlide.id}"]`);
                          if (slideElement) {
                            try {
                              const slideRect = slideElement.getBoundingClientRect();
                              const currentScroll = window.pageYOffset || document.documentElement.scrollTop || 0;
                              const targetScroll = currentScroll + slideRect.top - 100; // 100px offset from top for better visibility
                              
                              if (typeof targetScroll === 'number' && !isNaN(targetScroll)) {
                                window.scrollTo({
                                  top: targetScroll,
                                  behavior: 'smooth'
                                });
                              }
                            } catch (error) {
                              console.error('Error scrolling to moved slide:', error);
                            }
                          }
                        }, 100); // DOM 업데이트 후 스크롤
                      }
                    }}
                    className={`p-1 rounded text-xs transition-all ${
                      currentSlideIndex > 0
                        ? isDarkMode 
                          ? 'hover:bg-gray-700 text-gray-400 hover:text-white' 
                          : 'hover:bg-gray-300 text-gray-600 hover:text-gray-900'
                        : 'text-gray-400 cursor-not-allowed opacity-50'
                    }`}
                    disabled={currentSlideIndex === 0}
                  >
                    ▲
                  </button>
                  <button
                    onClick={() => {
                      if (currentSlideIndex < slides.length - 1) {
                        onSlideMove(currentSlideIndex, currentSlideIndex + 1);
                        // 슬라이드 이동 후 자동 스크롤
                        setTimeout(() => {
                          const slideElement = document.querySelector(`[data-slide-id="${tempSlide.id}"]`);
                          if (slideElement) {
                            try {
                              const slideRect = slideElement.getBoundingClientRect();
                              const currentScroll = window.pageYOffset || document.documentElement.scrollTop || 0;
                              const targetScroll = currentScroll + slideRect.top - 100; // 100px offset from top for better visibility
                              
                              if (typeof targetScroll === 'number' && !isNaN(targetScroll)) {
                                window.scrollTo({
                                  top: targetScroll,
                                  behavior: 'smooth'
                                });
                              }
                            } catch (error) {
                              console.error('Error scrolling to moved slide:', error);
                            }
                          }
                        }, 100); // DOM 업데이트 후 스크롤
                      }
                    }}
                    className={`p-1 rounded text-xs transition-all ${
                      currentSlideIndex === slides.length - 1
                        ? 'text-gray-400 cursor-not-allowed opacity-50'
                        : isDarkMode 
                          ? 'hover:bg-gray-700 text-gray-400 hover:text-white' 
                          : 'hover:bg-gray-300 text-gray-600 hover:text-gray-900'
                    }`}
                    disabled={currentSlideIndex === slides.length - 1}
                  >
                    ▼
                  </button>
                </div>
              </div>
              
              {/* 햄버거 메뉴 버튼 제거 - 테마/배경 버튼을 직접 표시 */}
              <div className="flex items-center gap-1">
                <button
                  onClick={() => onActiveSectionChange('theme')}
                  className={`px-3 py-2 rounded-md text-xs font-medium transition-all flex items-center gap-1 ${
                    activeSection === 'theme'
                      ? isDarkMode
                        ? 'bg-blue-600 text-white'
                        : 'bg-blue-500 text-white'
                      : isDarkMode
                        ? 'hover:bg-gray-800 text-gray-400'
                        : 'hover:bg-gray-200 text-gray-600'
                  }`}
                >
                  <Palette className="w-4 h-4" />
                  <span className="hidden sm:inline">{language === 'ko' ? '테마' : 'Theme'}</span>
                </button>
                <button
                  onClick={() => onActiveSectionChange('background')}
                  className={`px-3 py-2 rounded-md text-xs font-medium transition-all flex items-center gap-1 ${
                    activeSection === 'background'
                      ? isDarkMode
                        ? 'bg-blue-600 text-white'
                        : 'bg-blue-500 text-white'
                      : isDarkMode
                        ? 'hover:bg-gray-800 text-gray-400'
                        : 'hover:bg-gray-200 text-gray-600'
                  }`}
                >
                  <Image className="w-4 h-4" />
                  <span className="hidden sm:inline">{language === 'ko' ? '배경' : 'Background'}</span>
                </button>
              </div>
            </div>

            <div className="flex items-center gap-2 flex-nowrap">
              {/* 저장 버튼 - 프롬프트 왼쪽으로 이동, 모바일에서 숨김 */}
              <button
                onClick={onSaveAndExit}
                className="px-2 sm:px-4 py-2 bg-green-500 text-white hover:bg-green-600 rounded-lg transition-all text-sm font-medium whitespace-nowrap flex items-center justify-center"
                title={language === 'ko' ? '저장 후 닫기' : 'Save & Exit'}
              >
                <span className="sm:hidden">💾</span>
                <span className="hidden sm:inline">{language === 'ko' ? '저장' : 'SAVE'}</span>
              </button>

              <button
                onClick={onPromptPanelToggle}
                className={`p-2 rounded-md transition-all flex items-center gap-1 ${
                  showPromptPanel
                    ? 'bg-purple-600 text-white'
                    : isDarkMode 
                      ? 'hover:bg-gray-800 text-gray-400' 
                      : 'hover:bg-gray-200 text-gray-600'
                }`}
                title={language === 'ko' ? '프롬프트' : 'Prompt'}
              >
                <MessageSquare className="w-4 h-4" />
                <span className="hidden sm:inline text-xs">{language === 'ko' ? '프롬프트' : 'Prompt'}</span>
              </button>
              <button
                onClick={onLayerPanelOpen}
                className={`p-2 rounded-md transition-all flex items-center gap-1 ${
                  isDarkMode 
                    ? 'hover:bg-gray-800 text-gray-400' 
                    : 'hover:bg-gray-200 text-gray-600'
                }`}
                title={language === 'ko' ? '레이어' : 'Layers'}
              >
                <Layers className="w-4 h-4" />
                <span className="hidden sm:inline text-xs">{language === 'ko' ? '레이어' : 'Layers'}</span>
              </button>
              
              {/* 새 콘텐츠 생성 버튼 */}
              <button
                onClick={() => {
                  // 우측 패널 토글 기능을 위해 상위 컴포넌트에서 처리
                  // 여기서는 이벤트만 전달
                  if (onRightPanelToggle) {
                    onRightPanelToggle();
                  }
                }}
                className={`p-2 rounded-md transition-all flex items-center gap-1 ${
                  isDarkMode 
                    ? 'hover:bg-gray-800 text-gray-400' 
                    : 'hover:bg-gray-200 text-gray-600'
                }`}
                title={language === 'ko' ? '새 콘텐츠 생성' : 'New Content'}
              >
                <PlusCircle className="w-4 h-4" />
                <span className="hidden sm:inline text-xs">{language === 'ko' ? '생성' : 'New'}</span>
              </button>

              <div className="hidden sm:flex rounded-lg overflow-hidden">
                <button
                  onClick={() => onTabChange('preview')}
                  className={`px-3 py-2 text-xs font-bold transition-all ${
                    activeTab === 'preview'
                      ? 'bg-blue-500 text-white'
                      : isDarkMode ? 'bg-gray-800 text-gray-400' : 'bg-gray-200 text-gray-600'
                  }`}
                  title="WYSIWYG"
                >
                  W
                </button>
                <button
                  onClick={() => onTabChange('code')}
                  className={`px-3 py-2 text-xs font-bold transition-all ${
                    activeTab === 'code'
                      ? 'bg-blue-500 text-white'
                      : isDarkMode ? 'bg-gray-800 text-gray-400' : 'bg-gray-200 text-gray-600'
                  }`}
                  title="HTML"
                >
                  H
                </button>
              </div>

              {/* 크기 조절 - 모바일에서 숨김 */}
              <div className="hidden md:flex items-center gap-1">
                <button
                  onClick={() => onDisplayScaleChange(Math.max(50, displayScale - 5))}
                  className={`p-2 rounded transition-all text-sm font-bold ${
                    displayScale <= 50
                      ? 'opacity-50 cursor-not-allowed'
                      : isDarkMode ? 'hover:bg-gray-800 text-gray-400' : 'hover:bg-gray-200 text-gray-600'
                  }`}
                  disabled={displayScale <= 50}
                  title="Zoom Out"
                >
                  −
                </button>
                  <span className={`text-xs min-w-[3rem] text-center ${
                    isDarkMode ? 'text-gray-500' : 'text-gray-600'
                  }`}>
                    {displayScale}%
                  </span>
                <button
                  onClick={() => onDisplayScaleChange(Math.min(150, displayScale + 5))}
                  className={`p-2 rounded transition-all text-sm font-bold ${
                    displayScale >= 150
                      ? 'opacity-50 cursor-not-allowed'
                      : isDarkMode ? 'hover:bg-gray-800 text-gray-400' : 'hover:bg-gray-200 text-gray-600'
                  }`}
                  disabled={displayScale >= 150}
                  title="Zoom In"
                >
                  +
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* 중앙: 메인 컨텐츠 영역 */}
        <div className="flex-1 flex flex-col rounded-lg h-auto">
          {/* 데스크톱에서만 탭 바 표시 */}
            <div className={`hidden md:flex items-center justify-between p-2 ${
              isDarkMode ? 'bg-gray-900' : 'bg-gray-100'
            } overflow-x-auto`}
            >
              <div className="flex items-center gap-2 flex-nowrap">
                {/* 슬라이드 네비게이션 - 가장 좌측에 배치 */}
                <div className={`flex flex-col items-center gap-1 p-1 rounded ${
                  isDarkMode ? 'bg-gray-800' : 'bg-gray-200'
                }`}>
                  <div className="flex items-center">
                    <span className={`text-sm font-light ${
                      isDarkMode ? 'text-blue-400' : 'text-blue-600'
                    }`}>
                      {currentSlideIndex + 1}
                    </span>
                    <span className={`text-[8px] ml-1 ${
                      isDarkMode ? 'text-gray-500' : 'text-gray-600'
                    }`}>
                      / {slides.length}
                    </span>
                  </div>
                  <div className="flex gap-1">
                    <button
                      onClick={() => {
                        if (currentSlideIndex > 0) {
                          onSlideMove(currentSlideIndex, currentSlideIndex - 1);
                          // 슬라이드 이동 후 자동 스크롤
                          setTimeout(() => {
                            const slideElement = document.querySelector(`[data-slide-id="${tempSlide.id}"]`);
                            if (slideElement) {
                              try {
                                const slideRect = slideElement.getBoundingClientRect();
                                const currentScroll = window.pageYOffset || document.documentElement.scrollTop || 0;
                                const targetScroll = currentScroll + slideRect.top - 100; // 100px offset from top for better visibility
                                
                                if (typeof targetScroll === 'number' && !isNaN(targetScroll)) {
                                  window.scrollTo({
                                    top: targetScroll,
                                    behavior: 'smooth'
                                  });
                                }
                              } catch (error) {
                                console.error('Error scrolling to moved slide:', error);
                              }
                            }
                          }, 100); // DOM 업데이트 후 스크롤
                        }
                      }}
                      className={`p-1 rounded text-xs transition-all ${
                        currentSlideIndex > 0
                          ? isDarkMode 
                            ? 'hover:bg-gray-700 text-gray-400 hover:text-white' 
                            : 'hover:bg-gray-300 text-gray-600 hover:text-gray-900'
                          : 'text-gray-400 cursor-not-allowed opacity-50'
                      }`}
                      disabled={currentSlideIndex === 0}
                    >
                      ▲
                    </button>
                    <button
                      onClick={() => {
                        if (currentSlideIndex < slides.length - 1) {
                          onSlideMove(currentSlideIndex, currentSlideIndex + 1);
                          // 슬라이드 이동 후 자동 스크롤
                          setTimeout(() => {
                            const slideElement = document.querySelector(`[data-slide-id="${tempSlide.id}"]`);
                            if (slideElement) {
                              try {
                                const slideRect = slideElement.getBoundingClientRect();
                                const currentScroll = window.pageYOffset || document.documentElement.scrollTop || 0;
                                const targetScroll = currentScroll + slideRect.top - 100; // 100px offset from top for better visibility
                                
                                if (typeof targetScroll === 'number' && !isNaN(targetScroll)) {
                                  window.scrollTo({
                                    top: targetScroll,
                                    behavior: 'smooth'
                                  });
                                }
                              } catch (error) {
                                console.error('Error scrolling to moved slide:', error);
                              }
                            }
                          }, 100); // DOM 업데이트 후 스크롤
                        }
                      }}
                      className={`p-1 rounded text-xs transition-all ${
                        currentSlideIndex === slides.length - 1
                          ? 'text-gray-400 cursor-not-allowed opacity-50'
                          : isDarkMode 
                            ? 'hover:bg-gray-700 text-gray-400 hover:text-white' 
                            : 'hover:bg-gray-300 text-gray-600 hover:text-gray-900'
                      }`}
                      disabled={currentSlideIndex === slides.length - 1}
                    >
                      ▼
                    </button>
                  </div>
                </div>
                
                {/* 햄버거 메뉴 버튼 제거 - 테마/배경 버튼을 직접 표시 */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => onActiveSectionChange('theme')}
                    className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all flex items-center gap-1.5 ${
                      activeSection === 'theme'
                        ? isDarkMode
                          ? 'bg-blue-600 text-white'
                          : 'bg-blue-500 text-white'
                        : isDarkMode
                          ? 'hover:bg-gray-800 text-gray-400'
                          : 'hover:bg-gray-200 text-gray-600'
                    }`}
                  >
                    <Palette className="w-4 h-4" />
                    <span className="hidden sm:inline">{language === 'ko' ? '테마' : 'Theme'}</span>
                  </button>
                  <button
                    onClick={() => onActiveSectionChange('background')}
                    className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all flex items-center gap-1.5 ${
                      activeSection === 'background'
                        ? isDarkMode
                          ? 'bg-blue-600 text-white'
                          : 'bg-blue-500 text-white'
                        : isDarkMode
                          ? 'hover:bg-gray-800 text-gray-400'
                          : 'hover:bg-gray-200 text-gray-600'
                    }`}
                  >
                    <Image className="w-4 h-4" />
                    <span className="hidden sm:inline">{language === 'ko' ? '배경' : 'Background'}</span>
                  </button>
                </div>
              </div>

              <div className="flex items-center gap-2 flex-nowrap">
                {/* 저장 버튼 - 프롬프트 왼쪽으로 이동, 모바일에서 숨김 */}
                <button
                  onClick={onSaveAndExit}
                  className="px-2 sm:px-4 py-2 bg-green-500 text-white hover:bg-green-600 rounded-lg transition-all text-sm font-medium whitespace-nowrap flex items-center justify-center"
                  title={language === 'ko' ? '저장 후 닫기' : 'Save & Exit'}
                >
                  <span className="sm:hidden">💾</span>
                  <span className="hidden sm:inline">{language === 'ko' ? '저장' : 'SAVE'}</span>
                </button>

                <button
                  onClick={onPromptPanelToggle}
                  className={`p-2 rounded-md transition-all flex items-center gap-1.5 ${
                    showPromptPanel
                      ? 'bg-purple-600 text-white'
                      : isDarkMode 
                        ? 'hover:bg-gray-800 text-gray-400' 
                        : 'hover:bg-gray-200 text-gray-600'
                  }`}
                  title={language === 'ko' ? '프롬프트' : 'Prompt'}
                >
                  <MessageSquare className="w-4 h-4" />
                  <span className="hidden sm:inline text-xs">{language === 'ko' ? '프롬프트' : 'Prompt'}</span>
                </button>
                <button
                  onClick={onLayerPanelOpen}
                  className={`p-2 rounded-md transition-all flex items-center gap-1.5 ${
                    isDarkMode 
                      ? 'hover:bg-gray-800 text-gray-400' 
                      : 'hover:bg-gray-200 text-gray-600'
                  }`}
                  title={language === 'ko' ? '레이어' : 'Layers'}
                >
                  <Layers className="w-4 h-4" />
                  <span className="hidden sm:inline text-xs">{language === 'ko' ? '레이어' : 'Layers'}</span>
                </button>
                
                {/* 새 콘텐츠 생성 버튼 */}
                <button
                  onClick={() => {
                    // 우측 패널 토글 기능을 위해 상위 컴포넌트에서 처리
                    // 여기서는 이벤트만 전달
                    if (onRightPanelToggle) {
                      onRightPanelToggle();
                    }
                  }}
                  className={`p-2 rounded-md transition-all flex items-center gap-1.5 ${
                    isDarkMode 
                      ? 'hover:bg-gray-800 text-gray-400' 
                      : 'hover:bg-gray-200 text-gray-600'
                  }`}
                  title={language === 'ko' ? '새 콘텐츠 생성' : 'New Content'}
                >
                  <Plus className="w-4 h-4" />
                  <span className="hidden sm:inline text-xs">{language === 'ko' ? '새 콘텐츠' : 'New'}</span>
                </button>
                
                {/* 위지윅/HTML 버튼 - 모바일에서 숨김 */}
                <div className="hidden sm:flex rounded-lg overflow-hidden">
                  <button
                    onClick={() => onTabChange('preview')}
                    className={`px-3 py-2 text-xs font-bold transition-all ${
                      activeTab === 'preview'
                        ? 'bg-blue-500 text-white'
                        : isDarkMode ? 'bg-gray-800 text-gray-400' : 'bg-gray-200 text-gray-600'
                    }`}
                    title="WYSIWYG"
                  >
                    W
                  </button>
                  <button
                    onClick={() => onTabChange('code')}
                    className={`px-3 py-2 text-xs font-bold transition-all ${
                      activeTab === 'code'
                        ? 'bg-blue-500 text-white'
                        : isDarkMode ? 'bg-gray-800 text-gray-400' : 'bg-gray-200 text-gray-600'
                    }`}
                    title="HTML"
                  >
                    H
                  </button>
                </div>

                {/* 크기 조절 - 모바일에서 숨김 */}
                <div className="hidden md:flex items-center gap-1">
                  <button
                    onClick={() => onDisplayScaleChange(Math.max(50, displayScale - 5))}
                    className={`p-2 rounded transition-all text-sm font-bold ${
                      displayScale <= 50
                        ? 'opacity-50 cursor-not-allowed'
                        : isDarkMode ? 'hover:bg-gray-800 text-gray-400' : 'hover:bg-gray-200 text-gray-600'
                    }`}
                    disabled={displayScale <= 50}
                    title="Zoom Out"
                  >
                    −
                  </button>
                  <span className={`text-xs min-w-[3rem] text-center ${
                    isDarkMode ? 'text-gray-500' : 'text-gray-600'
                  }`}>
                    {displayScale}%
                  </span>
                  <button
                    onClick={() => onDisplayScaleChange(Math.min(150, displayScale + 5))}
                    className={`p-2 rounded transition-all text-sm font-bold ${
                      displayScale >= 150
                        ? 'opacity-50 cursor-not-allowed'
                        : isDarkMode ? 'hover:bg-gray-800 text-gray-400' : 'hover:bg-gray-200 text-gray-600'
                    }`}
                    disabled={displayScale >= 150}
                    title="Zoom In"
                  >
                    +
                  </button>
                </div>
              </div>
            </div>

            {/* 컨텐츠 영역 - 모바일에서는 flex-col */}
            <div className="flex flex-col md:block p-1 rounded-lg" style={{ height: 'auto', flex: '1 1 auto' }}>
              {/* 테마 섹션 */}
              {activeSection === 'theme' && (
                <div className="flex flex-wrap gap-2 justify-center">
                  {/* 동적 레이아웃 - 모바일에서 숨김 */}
                  <div className={`hidden md:block w-fit p-1 rounded-lg border ${
                    isDarkMode 
                      ? 'bg-gray-800 border-gray-700' 
                      : 'bg-white border-gray-200'
                  }`}
                  >
                    {objectCount === 0 ? (
                      <div className={`p-1 rounded-lg border ${
                        isDarkMode 
                          ? 'bg-gray-800 border-gray-700 text-gray-300' 
                          : 'bg-gray-50 border-gray-200 text-gray-500'
                      }`}>
                        <div className="flex items-center gap-2 mb-2">
                          <span className="font-medium text-xs">{language === 'ko' ? '동적 레이아웃' : 'Dynamic Layout'}</span>
                        </div>
                        <p className="text-xs">{language === 'ko' ? '배치할 객체가 없습니다' : 'No objects to arrange'}</p>
                      </div>
                    ) : (
                      <>
                        <div className="flex items-center gap-2 mb-2">
                          <span className={`font-medium text-xs ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            {language === 'ko' ? '동적 레이아웃' : 'Dynamic Layout'}
                          </span>
                          <span className={`text-xs px-2 py-0.5 rounded ${
                            isDarkMode 
                              ? 'bg-gray-700 text-gray-300' 
                              : 'bg-gray-100 text-gray-600'
                          }`}>
                            {objectCount}
                          </span>
                        </div>

                        <div className="flex gap-1 mb-2">
                          {Array.from({ length: 5 }, (_, index) => {
                            const option = layoutOptions[index];
                            return (
                              <button
                                key={index}
                                onClick={() => option && onDynamicLayoutChange(option)}
                                disabled={!option}
                                className={`flex-shrink-0 p-0 rounded transition-colors ${
                                  option ? (
                                    selectedLayoutOption && selectedLayoutOption.type === option.type
                                      ? isDarkMode
                                        ? 'bg-blue-900/50 text-blue-300 border-blue-500'
                                        : 'bg-blue-50 text-blue-700 border-blue-200'
                                      : isDarkMode
                                        ? 'hover:bg-gray-700 text-gray-300 border-gray-600'
                                        : 'hover:bg-gray-50 text-gray-700 border-gray-200'
                                  ) : (
                                    isDarkMode
                                      ? 'bg-gray-800 text-gray-600 border-gray-700 cursor-not-allowed'
                                      : 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                                  )
                                } border`}
                              >
                                {option ? (
                                  <LayoutPreview 
                                    columns={option.columns} 
                                    isSelected={selectedLayoutOption?.type === option.type}
                                    isDarkMode={isDarkMode}
                                  />
                                ) : (
                                  <div className="w-6 h-6 border rounded flex items-center justify-center opacity-50">
                                    <span className="text-xs text-gray-400">-</span>
                                  </div>
                                )}
                              </button>
                            );
                          })}
                        </div>

                        <div className="flex items-center gap-1">
                          <div className="flex items-center gap-1">
                            <span className="text-xs">{language === 'ko' ? '간격' : 'Spacing'}:</span>
                            {['none', 'narrow', 'normal', 'wide'].map((spacing, idx) => (
                              <button
                                key={spacing}
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  onLayoutSpacingChange(spacing as 'none' | 'narrow' | 'normal' | 'wide');
                                }}
                                className={`w-6 h-6 text-xs rounded flex items-center justify-center transition-all border ${
                                  layoutSpacing === spacing
                                    ? 'bg-blue-500 text-white border-blue-500'
                                    : isDarkMode
                                      ? 'bg-gray-900 hover:bg-gray-800 text-gray-400 border-gray-700'
                                      : 'bg-gray-100 hover:bg-gray-200 text-gray-600 border-gray-300'
                                }`}
                                title={spacing}
                              >
                                {spacingIcons[idx]}
                              </button>
                            ))}
                          </div>
                          
                          {/* 제목 체크박스 숨김 (기능은 유지하되 UI에서만 숨김) */}
                          <label className="flex items-center cursor-pointer" style={{ display: 'none' }}>
                            <input
                              type="checkbox"
                              checked={includeTitleInLayout}
                              onChange={(e) => onIncludeTitleInLayoutChange(e.target.checked)}
                              className="w-3 h-3 accent-blue-500 rounded border border-gray-300 mr-1"
                            />
                            <span className={`text-xs ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}>
                              {language === 'ko' ? '제목' : 'Title'}
                            </span>
                          </label>
                        </div>
                      </>
                    )}
                  </div>

                  {/* 슬라이드 테마 - 모바일에서 숨김 */}
                  <div className={`hidden md:block w-fit p-1 rounded-lg border ${
                    isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                  }`}>
                    <h4 className={`text-xs font-medium mb-2 flex items-center gap-2 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                      {language === 'ko' ? '슬라이드 테마' : 'Slide Theme'}
                    </h4>
                    
                    <div className="flex gap-1">
                      {SLIDE_THEME_OPTIONS.map((option) => (
                        <button
                          key={option.id}
                          onClick={() => onSlideThemeChange(option.id)}
                          className={`
                            p-1.5 rounded transition-all flex flex-col items-center gap-0.5
                            ${(tempSlide.slideTheme as SlideTheme) === option.id
                              ? isDarkMode
                                ? 'bg-blue-600 text-white'
                                : 'bg-blue-500 text-white'
                              : isDarkMode
                                ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }
                          `}
                          title={option.description[language]}
                        >
                          {React.cloneElement(option.icon as React.ReactElement, { className: 'w-2 h-2' })}
                          <span className="text-[9px] font-medium">{option.name[language]}</span>
                        </button>
                      ))}
                    </div>

                    {(tempSlide.slideTheme as SlideTheme) !== 'default' && (
                      <div className="mt-1 space-y-1">
                        {/* 1행: 분할과 배경색을 2열로 배치 */}
                        <div className="grid grid-cols-2 gap-2">
                          {/* 1열: 분할 유형 */}
                          <div>
                            <div className="flex items-center gap-1">
                              <span className={`text-[10px] font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                {language === 'ko' ? '분할' : 'Divider'}
                              </span>
                              <div className="flex gap-0.5">
                                {dividerOptions.map((option) => (
                                  <button
                                    key={option.value}
                                    onClick={() => onDividerTypeChange(option.value as 'straight' | 'convex' | 'concave' | 'zigzag' | 'jagged' | 'gradient')}
                                    className={`w-4 h-4 text-xs rounded transition-all ${
                                      (tempSlide?.dividerType || 'straight') === option.value
                                        ? 'bg-blue-500 text-white'
                                        : isDarkMode
                                          ? 'bg-gray-700 hover:bg-gray-600 text-gray-400'
                                          : 'bg-gray-200 hover:bg-gray-300 text-gray-600'
                                    }`}
                                    title={option.label}
                                  >
                                    {option.icon}
                                  </button>
                                ))}
                              </div>
                            </div>
                          </div>
                          
                          {/* 2열: 배경색 */}
                          <div>
                            <div className="flex items-center gap-1">
                              <span className={`text-[10px] font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                {language === 'ko' ? '배경' : 'BG'}
                              </span>
                              <div className="grid grid-cols-4 gap-0.5">
                                {['#FFFFFF', '#E5E7EB', '#9CA3AF', '#374151'].map((color) => (
                                  <button
                                    key={color}
                                                                                                onClick={() => {
                                      setCustomColor(color);
                                      onContentBgColorChange(color);
                                    }}
                                    className={`w-4 h-4 rounded border transition-all ${
                                      (tempSlide.contentBgColor || '#E5E7EB') === color
                                        ? 'border-blue-500'
                                        : 'border-gray-300'
                                    }`}
                                    style={{ backgroundColor: color }}
                                    title={color}
                                  />
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        {/* 2행: 위치 막대바 (1,2열 통합) */}
                        <div>
                          <div className="flex items-center gap-2">
                            <span className={`text-[10px] font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                              {language === 'ko' ? '위치' : 'Position'}: {tempSlide.splitPosition || 25}%
                            </span>
                            <input
                              type="range"
                              min="20"
                              max="60"
                              step="10"
                              value={tempSlide.splitPosition || 25}
                              onChange={(e) => {
                                const newPosition = parseInt(e.target.value);
                                onSplitPositionChange(newPosition);
                              }}
                              className={`flex-1 h-1.5 rounded cursor-pointer ${
                                isDarkMode 
                                  ? 'bg-gray-700 accent-blue-500' 
                                  : 'bg-gray-200 accent-blue-500'
                              }`}
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* 테마 색상, 폰트 스타일, 박스 스타일 */}
                  <div
                    className="flex gap-1"
                  >
                    {/* 모바일용 5열 버튼 레이아웃 */}
                    <div className="md:hidden grid grid-cols-5 gap-1 w-full">
                      {/* 동적 레이아웃 */}
                      <div className="aspect-square">
                        <button
                          onClick={() => setShowDynamicLayoutModal(true)}
                          className={`w-full h-full rounded-lg border transition-colors flex flex-col ${
                            isDarkMode
                              ? 'bg-gray-700 border-gray-600 text-white hover:bg-gray-600'
                              : 'bg-white border-gray-300 text-gray-900 hover:bg-gray-50'
                          }`}
                        >
                          <div className="flex-1 flex items-center justify-center">
                            {selectedLayoutOption ? (
                              <LayoutPreview 
                                columns={selectedLayoutOption.columns} 
                                isSelected={true}
                                isDarkMode={isDarkMode}
                              />
                            ) : (
                              <Layout className="w-4 h-4" />
                            )}
                          </div>
                          <div className="px-1 py-1 text-[10px] text-center border-t">
                            {language === 'ko' ? '동적 레이아웃' : 'Dynamic Layout'}
                          </div>
                        </button>
                      </div>
                      
                      {/* 슬라이드 테마 */}
                      <div className="aspect-square">
                        <button
                          onClick={() => setShowSlideThemeModal(true)}
                          className={`w-full h-full rounded-lg border transition-colors flex flex-col ${
                            isDarkMode
                              ? 'bg-gray-700 border-gray-600 text-white hover:bg-gray-600'
                              : 'bg-white border-gray-300 text-gray-900 hover:bg-gray-50'
                          }`}
                        >
                          <div className="flex-1 flex flex-col items-center justify-center gap-1">
                            <div>
                              {SLIDE_THEME_OPTIONS.find(opt => opt.id === tempSlide.slideTheme)?.icon || <Palette className="w-4 h-4" />}
                            </div>
                            <span className={`text-[10px] font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                              {SLIDE_THEME_OPTIONS.find(opt => opt.id === tempSlide.slideTheme)?.name[language] || (language === 'ko' ? '기본' : 'Default')}
                            </span>
                          </div>
                          <div className="px-1 py-1 text-[10px] text-center border-t">
                            {language === 'ko' ? '슬라이드 테마' : 'Slide Theme'}
                          </div>
                        </button>
                      </div>
                      
                      {/* 테마 색상 */}
                      <div className="aspect-square">
                        <div className={`w-full h-full rounded-lg border flex flex-col ${
                          isDarkMode
                            ? 'bg-gray-700 border-gray-600'
                            : 'bg-white border-gray-300'
                        }`}>
                          <div className="flex-1 flex items-center justify-center">
                            <ThemeSelector
                              selectedTheme={theme}
                              onThemeChange={onThemeChange}
                              language={language}
                              isDarkMode={isDarkMode}
                              isMobileModalMode={true}
                            />
                          </div>
                          <div className="px-1 py-1 text-[10px] text-center border-t">
                            {language === 'ko' ? '테마 색상' : 'Theme Color'}
                          </div>
                        </div>
                      </div>
                      
                      {/* 폰트 스타일 */}
                      <div className="aspect-square">
                        <div className={`w-full h-full rounded-lg border flex flex-col ${
                          isDarkMode
                            ? 'bg-gray-700 border-gray-600'
                            : 'bg-white border-gray-300'
                        }`}>
                          <div className="flex-1 flex items-center justify-center">
                            <ThemeFontSelector
                              currentFont={(themeFont ?? THEME_FONTS[0]) as ThemeFont}
                              onFontChange={onThemeFontChange}
                              isDarkMode={isDarkMode}
                              language={language}
                              isMobileModalMode={true}
                            />
                          </div>
                          <div className="px-1 py-1 text-[10px] text-center border-t">
                            {language === 'ko' ? '폰트' : 'Font'}
                          </div>
                        </div>
                      </div>
                      
                      {/* 박스 스타일 */}
                      <div className="aspect-square">
                        <div className={`w-full h-full rounded-lg border flex flex-col ${
                          isDarkMode
                            ? 'bg-gray-700 border-gray-600'
                            : 'bg-white border-gray-300'
                        }`}>
                          <div className="flex-1 flex items-center justify-center">
                            <SlideBoxStyleSelector
                              selectedStyle={(slideBorderStyle ?? SLIDE_BORDER_STYLES[0]) as SlideBorderStyle}
                              onStyleChange={onSlideBorderStyleChange}
                              language={language}
                              isDarkMode={isDarkMode}
                              theme={theme}
                              shadowType={'none'}
                              onShadowChange={(shadow) => {}}
                              isMobileModalMode={true}
                            />
                          </div>
                          <div className="px-1 py-1 text-[10px] text-center border-t">
                            {language === 'ko' ? '박스 스타일' : 'Box Style'}
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* 데스크톱에서는 기존 방식 유지 */}
                    <div className="hidden md:flex gap-1 whitespace-nowrap">
                      {/* 테마 색상 */}
                      <div className={`flex-shrink-0 p-1 rounded-lg border ${
                        isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                      }`}>
                        <ThemeSelector
                          selectedTheme={theme}
                          onThemeChange={onThemeChange}
                          language={language}
                          isDarkMode={isDarkMode}
                        />
                      </div>
                    
                      {/* 폰트 스타일 */}
                      <div className={`flex-shrink-0 p-1 rounded-lg border ${
                        isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                    }`}>
                      <div className="flex flex-col gap-1">
                        <label className={`text-xs font-medium ${
                          isDarkMode ? 'text-gray-200' : 'text-gray-700'
                        }`}>
                          {language === 'ko' ? '폰트스타일' : 'Font Style'}
                        </label>
                        <div className="relative group">
                          <button
                            ref={fontDropdownRef}
                            className={`w-full px-3 py-1 text-sm rounded-lg border transition-colors ${
                              isDarkMode
                                ? 'bg-gray-700 border-gray-600 text-white hover:bg-gray-600'
                                : 'bg-white border-gray-300 text-gray-900 hover:bg-gray-50'
                            } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                            onClick={() => {
                              setFontDropdownOpen(v => !v);
                              setBoxStyleDropdownOpen(false); // 박스 스타일 드롭다운 닫기
                            }}
                            type="button"
                          >
                            {themeFont?.name || 'modern-clean'}
                          </button>
                          {fontDropdownOpen && (
                            <div className="fixed inset-0 z-50">
                              {/* 배경 오버레이 - 클릭 시 드롭다운 닫기 */}
                              <div 
                                className="absolute inset-0 bg-black bg-opacity-10" 
                                onClick={() => setFontDropdownOpen(false)}
                              />
                              <ul 
                                className={`absolute z-50 border rounded-lg shadow-lg min-w-[300px] max-h-96 overflow-y-auto ${
                                  isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                                }`}
                                style={{
                                  // 버튼 위치 기준으로 드롭다운 위치 설정 (우측 정렬)
                                  top: fontDropdownRef.current ? fontDropdownRef.current.getBoundingClientRect().bottom + window.scrollY + 'px' : '0px',
                                  left: fontDropdownRef.current ? (fontDropdownRef.current.getBoundingClientRect().right - 300) + window.scrollX + 'px' : '0px',
                                }}
                              >
                                {THEME_FONTS.map(font => (
                                  <li
                                    key={font.id}
                                    className={`px-4 py-3 cursor-pointer transition-colors first:rounded-t-lg last:rounded-b-lg ${
                                      isDarkMode ? 'hover:bg-gray-700 text-gray-200' : 'hover:bg-blue-100 text-gray-900'
                                    }`}
                                    onClick={() => {
                                      onThemeFontChange(font);
                                      setFontDropdownOpen(false);
                                    }}
                                  >
                                    <div className="flex items-start gap-3">
                                      <div className="flex-1 min-w-0">
                                        <div
                                          className="text-base font-medium mb-2"
                                          style={{
                                            fontFamily: font.fontFamily,
                                            fontWeight: font.effects.fontWeight,
                                            letterSpacing: font.effects.letterSpacing,
                                            textShadow: font.effects.textShadow,
                                          }}
                                        >
                                          {font.name}
                                        </div>
                                        <div
                                          className="text-sm opacity-70 leading-relaxed break-words"
                                          style={{
                                            fontFamily: font.fontFamily,
                                            fontWeight: font.effects.fontWeight,
                                            letterSpacing: font.effects.letterSpacing,
                                            textShadow: font.effects.textShadow,
                                          }}
                                        >
                                          {language === 'ko' ? '안녕하세요! Hello World!' : 'Hello World! 안녕하세요!'}
                                        </div>
                                      </div>
                                      <div className="flex-shrink-0">
                                        <div
                                          className="w-12 h-10 rounded-lg border-2 flex items-center justify-center text-sm font-bold"
                                          style={{
                                            fontFamily: font.fontFamily,
                                            fontWeight: font.effects.fontWeight,
                                            letterSpacing: font.effects.letterSpacing,
                                            textShadow: font.effects.textShadow,
                                            backgroundColor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
                                            borderColor: isDarkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)',
                                          }}
                                        >
                                          Aa
                                        </div>
                                      </div>
                                    </div>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                        
                        {/* 폰트 효과 */}
                        <div className="flex items-center gap-2 mt-1">
                          <label className="flex items-center gap-1.5 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={tempSlide.fontBorder || false}
                              onChange={(e) => {
                                const enabled = e.target.checked;
                                onFontBorderChange?.(enabled);
                              }}
                              className="w-3 h-3 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <span className={`text-xs ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                              {language === 'ko' ? '테두리' : 'Border'}
                            </span>
                          </label>
                          
                          <label className="flex items-center gap-1.5 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={tempSlide.fontGlow || false}
                              onChange={(e) => {
                                const enabled = e.target.checked;
                                onFontGlowChange?.(enabled);
                              }}
                              className="w-3 h-3 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <span className={`text-xs ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                              {language === 'ko' ? '광선' : 'Glow'}
                            </span>
                          </label>
                        </div>
                      </div>
                    </div>
                    
                    {/* 박스 스타일 */}
                    <div className={`flex-shrink-0 p-1 rounded-lg border ${
                      isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                    }`}>
                      <div className="flex flex-col gap-1">
                        <label className={`text-xs font-medium ${
                          isDarkMode ? 'text-gray-200' : 'text-gray-700'
                        }`}>
                          {language === 'ko' ? '박스스타일' : 'Box Style'}
                        </label>
                        <div className="relative group">
                          <button
                            ref={boxStyleDropdownRef}
                            className={`w-full px-3 py-1 text-sm rounded-lg border transition-colors ${
                              isDarkMode
                                ? 'bg-gray-700 border-gray-600 text-white hover:bg-gray-600'
                                : 'bg-white border-gray-300 text-gray-900 hover:bg-gray-50'
                            } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                            onClick={() => {
                              setBoxStyleDropdownOpen(v => !v);
                              setFontDropdownOpen(false); // 폰트 드롭다운 닫기
                            }}
                            type="button"
                          >
                            {(slideBorderStyle?.name && t[slideBorderStyle.name as keyof typeof t]) || t.noStyle || 'No Style'}
                          </button>
                          {boxStyleDropdownOpen && (
                            <div className="fixed inset-0 z-50">
                              {/* 배경 오버레이 - 클릭 시 드롭다운 닫기 */}
                              <div 
                                className="absolute inset-0 bg-black bg-opacity-10" 
                                onClick={() => setBoxStyleDropdownOpen(false)}
                              />
                              <ul 
                                className={`absolute z-50 border rounded-lg shadow-lg min-w-[300px] max-h-96 overflow-y-auto ${
                                  isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                                }`}
                                style={{
                                  // 버튼 위치 기준으로 드롭다운 위치 설정 (우측 정렬)
                                  top: boxStyleDropdownRef.current ? boxStyleDropdownRef.current.getBoundingClientRect().bottom + window.scrollY + 'px' : '0px',
                                  left: boxStyleDropdownRef.current ? (boxStyleDropdownRef.current.getBoundingClientRect().right - 300) + window.scrollX + 'px' : '0px',
                                }}
                              >
                                {SLIDE_BORDER_STYLES.map(style => (
                                  <li
                                    key={style.id}
                                    className={`px-4 py-3 cursor-pointer transition-colors first:rounded-t-lg last:rounded-b-lg ${
                                      isDarkMode ? 'hover:bg-gray-700 text-gray-200' : 'hover:bg-blue-100 text-gray-900'
                                    }`}
                                    onClick={() => {
                                      onSlideBorderStyleChange(style);
                                      setBoxStyleDropdownOpen(false);
                                    }}
                                  >
                                    <div className="flex items-start gap-3">
                                      <div className="flex-1 min-w-0">
                                        <div className="text-base font-medium mb-2">
                                          {t[style.name as keyof typeof t] || style.name}
                                        </div>
                                        <div className="text-sm opacity-70 leading-relaxed break-words">
                                          {language === 'ko' ? '박스 스타일 미리보기' : 'Box Style Preview'}
                                        </div>
                                      </div>
                                      <div className="flex-shrink-0">
                                        <div 
                                          className="w-12 h-10 rounded-lg border-2 flex items-center justify-center"
                                          style={{
                                            borderWidth: style.borderWidth,
                                            borderStyle: style.borderStyle,
                                            borderColor: style.id === 'bold-frame' && theme ? theme.accent : (isDarkMode ? '#8B5CF6' : '#6366f1'),
                                            borderRadius: style.borderRadius,
                                            boxShadow: style.boxShadow,
                                            backgroundColor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
                                          }}
                                        />
                                      </div>
                                    </div>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
            </div>
          )}

          {/* 배경 섹션 */}
          {activeSection === 'background' && (
            <>
              <div className="background-controller-wrapper">
                <BackgroundController
                  currentSeed={getCurrentBackgroundSeed() || ''}
                  blur={tempSlide.backgroundBlur || 2}
                  grayscale={tempSlide.backgroundGrayscale || false}
                  width={aspectRatio.width}
                  height={aspectRatio.height}
                  backgroundType={tempSlide.backgroundType || 'image'}
                  previousSlideBackgroundSeed={getPreviousSlideBackgroundSeed() || ''}
                  {...(slides.length > 0 && {
                    firstSlideBackground: {
                      type: slides[0]?.backgroundType || 'image',
                      ...(() => {
                        const firstSlide = slides[0];
                        if (firstSlide?.backgroundImage && firstSlide.backgroundImage.includes('picsum.photos/seed/')) {
                          const seedMatch = firstSlide.backgroundImage.match(/seed\/([^/]+)\//); 
                          const seed = seedMatch ? seedMatch[1] : firstSlide?.backgroundSeed;
                          return seed ? { seed } : {};
                        }
                        return firstSlide?.backgroundSeed ? { seed: firstSlide.backgroundSeed } : {};
                      })(),
                      ...(slides[0]?.backgroundColor && { color: slides[0].backgroundColor }),
                      ...(slides[0]?.animatedBackgroundEffect && { animatedEffect: slides[0].animatedBackgroundEffect }),
                      ...(slides[0]?.animatedBackgroundOptions && { animatedOptions: slides[0].animatedBackgroundOptions })
                    }
                  })}
                  currentPattern={tempSlide.backgroundPattern || 'none'}
                  animatedEffect={tempSlide.animatedBackgroundEffect || 'birds'}
                  animatedOptions={tempSlide.animatedBackgroundOptions || {}}
                  onSeedChange={onBackgroundSeedChange}
                  onBlurChange={onBackgroundBlurChange}
                  onGrayscaleChange={onBackgroundGrayscaleChange}
                  onBackgroundChange={onBackgroundChange}
                  onPatternChange={onBackgroundPatternChange}
                  onBackgroundTypeChange={onBackgroundTypeChange}
                  onAnimatedEffectChange={onAnimatedEffectChange}
                  onAnimatedOptionsChange={onAnimatedOptionsChange}
                  onImageElementAdd={onImageElementAdd}
                  isDarkMode={isDarkMode}
                  language={language}
                  slideKeywords={tempSlide.keywords || []}
                />
              </div>
            </>
          )}
        </div>
        
        {/* PC 모드 요소 추가 섹션 - 테마/배경 섹션 아래 */}
        <div className="hidden md:block mt-2">
          <div className="w-full flex justify-start items-center min-h-8 sm:min-h-6 bg-transparent rounded-b-md px-2 sm:px-4">
            <div className="flex justify-center items-center gap-2 w-full h-full">
              <button
                onClick={onTextElementAdd}
                className="w-[90px] h-6 bg-green-500 text-white hover:bg-green-600 rounded transition-all flex items-center justify-center gap-1 text-xs"
              >
                <Type className="w-3 h-3" />
                <span className="font-medium">{language === 'ko' ? '텍스트' : 'Text'}</span>
              </button>
              <button
                onClick={onShapeElementAdd}
                className="w-[90px] h-6 bg-indigo-500 text-white hover:bg-indigo-600 rounded transition-all flex items-center justify-center gap-1 text-xs"
              >
                <Shapes className="w-3 h-3" />
                <span className="font-medium">{language === 'ko' ? '도형' : 'Shape'}</span>
              </button>
              <button
                onClick={onImageElementUpload}
                className="w-[90px] h-6 bg-blue-500 text-white hover:bg-blue-600 rounded transition-all flex items-center justify-center gap-1 text-xs"
              >
                <Image className="w-3 h-3" />
                <span className="font-medium">{language === 'ko' ? '이미지' : 'Image'}</span>
              </button>
              <button
                onClick={onVideoElementUpload}
                className="w-[90px] h-6 bg-red-500 text-white hover:bg-red-600 rounded transition-all flex items-center justify-center gap-1 text-xs"
              >
                <Video className="w-3 h-3" />
                <span className="font-medium">{language === 'ko' ? '동영상' : 'Video'}</span>
              </button>
              <button
                onClick={onChartElementAdd}
                className="w-[90px] h-6 bg-purple-500 text-white hover:bg-purple-600 rounded transition-all flex items-center justify-center gap-1 text-xs"
              >
                <BarChart3 className="w-3 h-3" />
                <span className="font-medium">{language === 'ko' ? '차트' : 'Chart'}</span>
              </button>
            </div>
          </div>
        </div>
            
          {/* 요소 추가 섹션 - 모바일에서만 전체 너비 */}
          <div className="block md:hidden w-full flex justify-start items-center min-h-12 sm:min-h-12 bg-transparent rounded-b-md px-2 sm:px-4">
            <div className="grid grid-cols-5 gap-2 rounded-b-md w-full">
              <button
                onClick={onTextElementAdd}
                className="flex-1 sm:flex-shrink-0 px-2 py-3 sm:py-1 bg-green-500 text-white hover:bg-green-600 rounded transition-all flex items-center justify-center sm:justify-start gap-1 text-xs"
              >
                <Type className="w-3 h-3 flex-shrink-0" />
                <span className="font-medium whitespace-nowrap">{language === 'ko' ? '텍스트' : 'Text'}</span>
              </button>
              <button
                onClick={onShapeElementAdd}
                className="flex-1 sm:flex-shrink-0 px-2 py-3 sm:py-1 bg-indigo-500 text-white hover:bg-indigo-600 rounded transition-all flex items-center justify-center sm:justify-start gap-1 text-xs"
              >
                <Shapes className="w-3 h-3 flex-shrink-0" />
                <span className="font-medium whitespace-nowrap">{language === 'ko' ? '도형' : 'Shape'}</span>
              </button>
              <button
                onClick={onImageElementUpload}
                className="flex-1 sm:flex-shrink-0 px-2 py-3 sm:py-1 bg-blue-500 text-white hover:bg-blue-600 rounded transition-all flex items-center justify-center sm:justify-start gap-1 text-xs"
              >
                <Image className="w-3 h-3 flex-shrink-0" />
                <span className="font-medium whitespace-nowrap">{language === 'ko' ? '이미지' : 'Image'}</span>
              </button>
              <button
                onClick={onVideoElementUpload}
                className="flex-1 sm:flex-shrink-0 px-2 py-3 sm:py-1 bg-red-500 text-white hover:bg-red-600 rounded transition-all flex items-center justify-center sm:justify-start gap-1 text-xs"
              >
                <Video className="w-3 h-3 flex-shrink-0" />
                <span className="font-medium whitespace-nowrap">{language === 'ko' ? '동영상' : 'Video'}</span>
              </button>
              <button
                onClick={onChartElementAdd}
                className="flex-1 sm:flex-shrink-0 px-2 py-3 sm:py-1 bg-purple-500 text-white hover:bg-purple-600 rounded transition-all flex items-center justify-center sm:justify-start gap-1 text-xs"
              >
                <BarChart3 className="w-3 h-3 flex-shrink-0" />
                <span className="font-medium whitespace-nowrap">{language === 'ko' ? '차트' : 'Chart'}</span>
              </button>
              </div>
            </div>
        </div>



        {/* 모바일 메뉴 제거 - 더 이상 필요 없음 */}
        
        {/* 동적 레이아웃 모달 */}
        {showDynamicLayoutModal && (
          <div className="fixed inset-0 z-50 pointer-events-none">
            {/* 모달 외부 영역 - 클릭 시 모달 닫힘 */}
            <div 
              className="absolute inset-0 bg-black bg-opacity-50 pointer-events-auto" 
              onClick={() => setShowDynamicLayoutModal(false)}
            />
            {/* 상단 모달 */}
            <div 
              className={`absolute top-0 left-0 right-0 h-[18vh] pointer-events-auto transform transition-transform duration-300 ${
                showDynamicLayoutModal ? 'translate-y-0' : '-translate-y-full'
              } ${
              isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
            } border-b shadow-lg flex flex-col`}>
              {/* 닫기 버튼 */}
              <button
                onClick={() => setShowDynamicLayoutModal(false)}
                className={`absolute top-2 right-2 p-1 rounded-lg transition-colors z-10 ${
                  isDarkMode
                    ? 'hover:bg-gray-700 text-gray-400'
                    : 'hover:bg-gray-100 text-gray-600'
                }`}
              >
                <X className="w-4 h-4" />
              </button>
              
              {/* 모달 콘텐츠 */}
              <div className="flex-1 overflow-y-auto p-3">
                <h3 className={`text-sm font-medium mb-2 flex items-center gap-2 ${
                  isDarkMode ? 'text-gray-200' : 'text-gray-800'
                }`}>
                  <Layout className="w-4 h-4" />
                  {language === 'ko' ? '동적 레이아웃 선택' : 'Select Dynamic Layout'}
                </h3>
                {objectCount === 0 ? (
                  <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    {language === 'ko' ? '배치할 객체가 없습니다' : 'No objects to arrange'}
                  </p>
                ) : (
                  <>
                    <div className="grid grid-cols-3 gap-2">
                      {layoutOptions.map((option, index) => (
                        <button
                          key={index}
                          onClick={() => {
                            onDynamicLayoutChange(option);
                            setShowDynamicLayoutModal(false);
                          }}
                          className={`
                            p-2 rounded-lg border transition-all flex flex-col items-center gap-2 relative
                            ${selectedLayoutOption && selectedLayoutOption.type === option.type
                              ? isDarkMode 
                                ? 'border-blue-400 bg-blue-900/20' 
                                : 'border-blue-500 bg-blue-50'
                              : isDarkMode
                                ? 'border-gray-600 hover:border-gray-500 hover:bg-gray-700'
                                : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                            }
                          `}
                        >
                          {selectedLayoutOption && selectedLayoutOption.type === option.type && (
                            <div className="absolute top-1 right-1 w-3 h-3 bg-blue-500 rounded-full flex items-center justify-center">
                              <div className="w-1.5 h-1.5 bg-white rounded-full"></div>
                            </div>
                          )}
                          <LayoutPreview 
                            columns={option.columns} 
                            isSelected={selectedLayoutOption?.type === option.type}
                            isDarkMode={isDarkMode}
                          />
                          <span className={`text-xs font-medium ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                            {option.name ? (typeof option.name === 'object' ? option.name[language as keyof typeof option.name] || '' : String(option.name)) : ''}
                          </span>
                        </button>
                      ))}
                    </div>
                    
                    {/* 간격 조절 슬라이더 */}
                    <div className="mt-4 flex items-center gap-2">
                      <span className={`text-sm font-medium whitespace-nowrap ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        {language === 'ko' ? '간격' : 'Spacing'}
                      </span>
                      <span className={`text-sm font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        {(() => {
                          const spacingMap = {
                            'none': 3,
                            'narrow': 3,
                            'normal': 5,
                            'wide': 10
                          };
                          return spacingMap[layoutSpacing] || 5;
                        })()}px
                      </span>
                      <input
                        type="range"
                        min="3"
                        max="10"
                        value={(() => {
                          const spacingMap = {
                            'none': 3,
                            'narrow': 3,
                            'normal': 5,
                            'wide': 10
                          };
                          return spacingMap[layoutSpacing] || 5;
                        })()}
                        onChange={(e) => {
                          const value = parseInt(e.target.value);
                          let newSpacing: 'none' | 'narrow' | 'normal' | 'wide' = 'normal';
                          if (value <= 3) newSpacing = 'narrow';
                          else if (value <= 7) newSpacing = 'normal';
                          else newSpacing = 'wide';
                          
                          onLayoutSpacingChange(newSpacing);
                        }}
                        className={`flex-1 h-1.5 rounded-lg appearance-none cursor-pointer ${
                          isDarkMode 
                            ? 'bg-gray-700 accent-blue-400' 
                            : 'bg-gray-200 accent-blue-600'
                        }`}
                      />
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
        
        {/* 슬라이드 테마 모달 */}
        {showSlideThemeModal && (
          <div className="fixed inset-0 z-50 pointer-events-none">
            {/* 상단 모달 */}
            <div 
              className={`absolute top-0 left-0 right-0 h-[18vh] pointer-events-auto transform transition-transform duration-300 ${
                showSlideThemeModal ? 'translate-y-0' : '-translate-y-full'
              } ${
                isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
              } border-b shadow-lg flex flex-col`}
            >
              {/* 닫기 버튼 */}
              <button
                onClick={() => setShowSlideThemeModal(false)}
                className={`absolute top-2 right-2 p-1 rounded-lg transition-colors z-10 ${
                  isDarkMode
                    ? 'hover:bg-gray-700 text-gray-400'
                    : 'hover:bg-gray-100 text-gray-600'
                }`}
              >
                <X className="w-4 h-4" />
              </button>
              
              {/* 모달 콘텐츠 */}
              <div className="flex-1 overflow-y-auto p-3">
                <h3 className={`text-sm font-medium mb-2 flex items-center gap-2 ${
                  isDarkMode ? 'text-gray-200' : 'text-gray-800'
                }`}>
                  <Layout className="w-4 h-4" />
                  {language === 'ko' ? '슬라이드 테마 선택' : 'Select Slide Theme'}
                </h3>
                
                <div className="grid grid-cols-3 gap-2">
                  {SLIDE_THEME_OPTIONS.map((option) => (
                    <button
                      key={option.id}
                      onClick={() => {
                        onSlideThemeChange(option.id);
                        setShowSlideThemeModal(false);
                      }}
                      className={`
                        p-2 rounded-lg border-2 transition-all flex flex-row items-center justify-center gap-2 min-h-[40px] relative
                        ${(tempSlide.slideTheme as SlideTheme) === option.id
                          ? isDarkMode 
                            ? 'border-blue-400 bg-blue-900/20' 
                            : 'border-blue-500 bg-blue-50'
                          : isDarkMode
                            ? 'border-gray-600 hover:border-gray-500 hover:bg-gray-700'
                            : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                        }
                      `}
                    >
                      {(tempSlide.slideTheme as SlideTheme) === option.id && (
                        <div className="absolute top-1 right-1 w-3 h-3 bg-blue-500 rounded-full flex items-center justify-center">
                          <div className="w-1.5 h-1.5 bg-white rounded-full"></div>
                        </div>
                      )}
                      <div className="text-lg">{option.icon}</div>
                      <span className={`text-xs font-medium ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                        {option.name[language]}
                      </span>
                    </button>
                  ))}
                </div>
                
                {/* 분할 위치 조절 - left, right, top, bottom 테마에서만 표시 */}
                {tempSlide.slideTheme && ['left-split', 'right-split', 'top-split', 'bottom-split'].includes(tempSlide.slideTheme as string) && (
                  <div className="mt-4 flex items-center gap-2">
                    <span className={`text-sm font-medium whitespace-nowrap ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      {language === 'ko' ? '분할 위치' : 'Split'}
                    </span>
                    <span className={`text-sm font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      {tempSlide.splitPosition || 25}%
                    </span>
                    <input
                      type="range"
                      min="20"
                      max="60"
                      step="10"
                      value={tempSlide.splitPosition || 25}
                      onChange={(e) => {
                        const newPosition = parseInt(e.target.value);
                        onSplitPositionChange(newPosition);
                      }}
                      className={`flex-1 h-1.5 rounded-lg appearance-none cursor-pointer ${
                        isDarkMode 
                          ? 'bg-gray-700 accent-blue-400' 
                          : 'bg-gray-200 accent-blue-600'
                      }`}
                    />
                  </div>
                )}
              
                {/* 분할선 타입 - 모든 split 테마에서 표시 */}
                {tempSlide.slideTheme && ['left-split', 'right-split', 'top-split', 'bottom-split', 'diagonal'].includes(tempSlide.slideTheme as string) && (
                  <div className="mt-4">
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        {language === 'ko' ? '분할선' : 'Divider'}
                      </span>
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      {(['straight', 'convex', 'concave', 'zigzag', 'jagged', 'gradient'] as DividerType[]).map((type) => (
                        <button
                          key={type}
                          onClick={() => onDividerTypeChange(type as 'straight' | 'convex' | 'concave' | 'zigzag' | 'jagged' | 'gradient')}
                          className={`px-2 py-1 text-xs rounded transition-colors ${
                            tempSlide.dividerType === type
                              ? isDarkMode
                                ? 'bg-blue-600 text-white'
                                : 'bg-blue-500 text-white'
                              : isDarkMode
                                ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                          }`}
                        >
                          {type === 'straight' && (language === 'ko' ? '직선' : 'Straight')}
                          {type === 'convex' && (language === 'ko' ? '볼록' : 'Convex')}
                          {type === 'concave' && (language === 'ko' ? '오목' : 'Concave')}
                          {type === 'zigzag' && (language === 'ko' ? '지그재그' : 'Zigzag')}
                          {type === 'jagged' && (language === 'ko' ? '톱니' : 'Jagged')}
                          {type === 'gradient' && (language === 'ko' ? '그라데이션' : 'Gradient')}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              
                {/* 컨텐츠 배경색 설정 - split 테마에서만 표시 */}
                {tempSlide.slideTheme && ['left-split', 'right-split', 'top-split', 'bottom-split', 'diagonal'].includes(tempSlide.slideTheme as string) && (
                  <div className="mt-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        {language === 'ko' ? '컨텐츠 배경' : 'Background'}
                      </span>
                    </div>
                    <div className="flex gap-1 items-center">
                      {['#1F2937', '#E5E7EB', '#EF4444', '#F59E0B', '#10B981', '#3B82F6', '#8B5CF6'].map((color) => (
                        <button
                          key={color}
                          onClick={() => {
                            setCustomColor(color);
                            onContentBgColorChange(color);
                          }}
                          className={`w-6 h-6 rounded border-2 transition-all ${
                            (tempSlide.contentBgColor || '#E5E7EB') === color
                              ? 'border-blue-500 ring-2 ring-blue-300'
                              : 'border-gray-300 hover:border-gray-400'
                          }`}
                          style={{ backgroundColor: color }}
                          title={color}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
            {/* 배경 클릭 영역 - 메인 화면은 클릭 가능 */}
            <div 
              className="absolute inset-0 pointer-events-auto"
              onClick={() => setShowSlideThemeModal(false)}
              style={{ top: '25%' }}
            />
          </div>
        )}
      </div>  
    </>
  );
};